/**
 * Browser-side auth state.
 *
 * The gateway sets an HttpOnly ``kos_session`` cookie on successful Google
 * OAuth callback (see ADR-0011). JS never sees the token; we keep the
 * decoded user / tenant identity in React context so route shells can
 * render names + role-gated controls without re-fetching ``/v1/me``
 * everywhere.
 *
 * The dashboard shell loader fetches ``/v1/me`` once and feeds the
 * resulting ``AuthState`` straight into ``<AuthProvider state={auth}>``.
 * Route revalidation (e.g. after ``/v1/me/active-team``) will re-run the
 * loader and supply a fresh value — there is intentionally no internal
 * ``useState`` so context consumers always see the latest loader data.
 */

import { type ReactNode, createContext, useContext, useMemo } from "react";

import type { MeResponse } from "@/lib/types";

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  /** Optional avatar URL. Google OAuth doesn't surface this through the
   *  gateway today, so this is reserved for future expansion. */
  image?: string | null;
}

export interface AuthTenant {
  org_id: string;
  team_id: string;
  team_slug: string;
  team_name: string;
  org_name: string;
  org_plan: string;
  role: string;
}

export interface AuthState {
  user: AuthUser;
  tenant: AuthTenant;
}

export interface AuthContextValue {
  /** Null when the user is signed out (e.g. on the public shell). */
  state: AuthState | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  state,
  children,
}: {
  state: AuthState | null;
  children: ReactNode;
}) {
  const value = useMemo<AuthContextValue>(() => ({ state }), [state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Read the current auth state. Returns null when unauthenticated. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/** Throwing variant for screens that already passed `requireAuth`. */
export function useAuthState(): AuthState {
  const { state } = useAuth();
  if (!state) {
    throw new Error("useAuthState called without an authenticated session");
  }
  return state;
}

export function meToAuthState(me: MeResponse): AuthState {
  return {
    user: { id: me.user.id, email: me.user.email, name: me.user.name },
    tenant: {
      org_id: me.tenant.org_id,
      team_id: me.tenant.team_id,
      team_slug: me.tenant.team_slug,
      team_name: me.tenant.team_name,
      org_name: me.tenant.org_name,
      org_plan: me.tenant.org_plan,
      role: me.tenant.role,
    },
  };
}
